/**
 * Formatting Utilities
 *
 * Functions for formatting dates, file sizes, and service-related data.
 *
 * CRITICAL CONSTRAINTS:
 * - TypeScript strict mode (NO 'any' types)
 * - User-friendly display formats
 * - Consistent formatting across app
 */

import { Timestamp } from 'firebase/firestore';
import { ServiceType } from '@/shared/types';

/**
 * Format Firestore Timestamp to readable date string
 *
 * Handles both Timestamp instances and serialized timestamp objects
 * (which occur when timestamps are returned from Firebase Callable Functions)
 *
 * @param timestamp - Firestore Timestamp or serialized timestamp object
 * @param includeTime - Whether to include time (default: false)
 * @returns Formatted date string
 */
export function formatDate(
  timestamp: Timestamp | { seconds: number; nanoseconds: number } | { _seconds: number; _nanoseconds: number } | null | undefined,
  includeTime: boolean = false
): string {
  // Handle null/undefined
  if (!timestamp) {
    return 'N/A';
  }

  // Convert to Date object
  let date: Date;

  try {
    if (timestamp instanceof Timestamp) {
      // Real Timestamp object from direct Firestore queries
      date = timestamp.toDate();
    } else if (
      typeof timestamp === 'object' &&
      '_seconds' in timestamp &&
      '_nanoseconds' in timestamp &&
      typeof timestamp._seconds === 'number'
    ) {
      // Serialized timestamp from Firebase Callable Functions (Firebase's actual format)
      // Reconstruct Timestamp object using _seconds and _nanoseconds
      date = new Timestamp(timestamp._seconds, timestamp._nanoseconds).toDate();
    } else if (
      typeof timestamp === 'object' &&
      'seconds' in timestamp &&
      'nanoseconds' in timestamp &&
      typeof timestamp.seconds === 'number'
    ) {
      // Alternative serialized timestamp format (fallback)
      // Reconstruct Timestamp object
      date = new Timestamp(timestamp.seconds, timestamp.nanoseconds).toDate();
    } else {
      // Invalid format
      console.error('Invalid timestamp format:', timestamp);
      return 'Invalid date';
    }
  } catch (error) {
    console.error('Error formatting timestamp:', error, timestamp);
    return 'Invalid date';
  }

  // Format the date
  if (includeTime) {
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Format file size in bytes to human-readable string
 *
 * @param bytes - File size in bytes
 * @returns Formatted size string (e.g., "2.5 MB")
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}

/**
 * Get user-friendly service name
 *
 * @param serviceType - Service type enum
 * @returns Display name for service
 */
export function getServiceName(serviceType: ServiceType): string {
  const serviceNames: Record<ServiceType, string> = {
    [ServiceType.DAY_TO_DUSK]: 'Day to Dusk',
    [ServiceType.ITEM_REMOVAL]: 'Item Removal',
    [ServiceType.STAGING]: 'Virtual Staging',
    [ServiceType.DESCRIPTION]: 'Property Description',
    [ServiceType.DESTAGING]: 'Virtual Destaging',
    [ServiceType.COLLAGE]: 'AI Dollhouse View',
    [ServiceType.WATERMARK]: 'Watermark Removal',
    [ServiceType.ENHANCEMENT]: 'General Enhancement',
  };

  return serviceNames[serviceType];
}

/**
 * Get service icon emoji
 *
 * @param serviceType - Service type enum
 * @returns Icon for service
 */
export function getServiceIcon(serviceType: ServiceType): string {
  const serviceIcons: Record<ServiceType, string> = {
    [ServiceType.DAY_TO_DUSK]: '🌅',
    [ServiceType.ITEM_REMOVAL]: '🧹',
    [ServiceType.STAGING]: '🛋️',
    [ServiceType.DESCRIPTION]: '📝',
    [ServiceType.DESTAGING]: '🏚️',
    [ServiceType.COLLAGE]: '🏠',
    [ServiceType.WATERMARK]: '💧',
    [ServiceType.ENHANCEMENT]: '✨',
  };

  return serviceIcons[serviceType];
}

/**
 * Get credit cost for service
 *
 * @param serviceType - Service type enum
 * @returns Credit cost
 */
export function getServiceCost(serviceType: ServiceType): number {
  const serviceCosts: Record<ServiceType, number> = {
    [ServiceType.DAY_TO_DUSK]: 5,
    [ServiceType.ITEM_REMOVAL]: 5,
    [ServiceType.STAGING]: 5,
    [ServiceType.DESCRIPTION]: 2,
    [ServiceType.DESTAGING]: 5,
    [ServiceType.COLLAGE]: 30,
    [ServiceType.WATERMARK]: 1,
    [ServiceType.ENHANCEMENT]: 5,
  };

  return serviceCosts[serviceType];
}

/**
 * Get max attempts for service
 *
 * @param serviceType - Service type enum
 * @returns Maximum allowed attempts
 */
export function getServiceMaxAttempts(serviceType: ServiceType): number {
  const maxAttempts: Record<ServiceType, number> = {
    [ServiceType.DAY_TO_DUSK]: 2,
    [ServiceType.ITEM_REMOVAL]: 2,
    [ServiceType.STAGING]: 3,
    [ServiceType.DESCRIPTION]: 1,
    [ServiceType.DESTAGING]: 2,
    [ServiceType.COLLAGE]: 1,
    [ServiceType.WATERMARK]: 1,
    [ServiceType.ENHANCEMENT]: 2,
  };

  return maxAttempts[serviceType];
}

/**
 * Format storage usage as percentage
 *
 * @param used - Storage used in bytes
 * @param limit - Storage limit in bytes
 * @returns Percentage as number (0-100)
 */
export function getStoragePercentage(used: number, limit: number): number {
  if (limit === 0) return 0;
  return Math.round((used / limit) * 100);
}

/**
 * Get storage tier warning level
 *
 * @param percentage - Storage usage percentage
 * @returns Tier level (null, 'warning', or 'danger')
 */
export function getStorageTier(percentage: number): 'safe' | 'warning' | 'danger' {
  if (percentage >= 100) return 'danger'; // Tier 2: Hard limit
  if (percentage >= 80) return 'warning'; // Tier 1: Warning
  return 'safe';
}

/**
 * Truncate text intelligently at sentence or word boundaries
 *
 * @param text - Text to truncate
 * @param maxLength - Maximum length
 * @returns Truncated text with ellipsis if needed
 */
export function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;

  // Try to truncate at sentence boundary
  const sentences = text.split(/[.!?]\s/);
  let truncated = '';

  for (const sentence of sentences) {
    if ((truncated + sentence).length > maxLength) break;
    truncated += sentence + '. ';
  }

  if (truncated.length > 0) {
    return truncated.trim();
  }

  // Fall back to word boundary
  const words = text.split(' ');
  truncated = '';

  for (const word of words) {
    if ((truncated + word).length > maxLength) break;
    truncated += word + ' ';
  }

  return truncated.trim() + '...';
}
